import Link from "@components/Link/Link";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { commaConjunction } from "@utilities/commaConjunction";
import { useSettings } from "@utilities/context/settings";
import clsx from "clsx";
import { format } from "date-fns";
import { FC } from "react";
import EditorsChoiceMedia, {
  EditorsChoiceMediaType,
} from "./EditorsChoiceMedia";

import { NextContentfulImage } from "@components/Image";
import styles from "./C281_EditorsChoice.module.scss";

export enum TileSize {
  SMALL = "SMALL",
  LARGE = "LARGE",
}

export interface tagData {
  displayNameAndParamValue?: string;
  queryParameter?: string;
  id?: string;
}

export interface EditorsChoiceTileType {
  title: string;
  date: Date;
  linkUrl: string;
  description: string;
  topic?: string;
  image?: ImageWrapperType;
  mediaToRender?: EditorsChoiceMediaType;
  isEvent?: boolean;
  authors?: string[];
  tags?: tagData[];
}

export interface EditorsChoiceTileProps {
  tile: EditorsChoiceTileType;
  size: TileSize;
  isBlue?: boolean;
  hasImage?: boolean;
  hasDescription?: boolean;
}

const EditorsChoiceTile: FC<EditorsChoiceTileProps> = ({
  tile,
  size,
  isBlue = false,
  hasImage = false,
  hasDescription = false,
}) => {
  const {
    title,
    linkUrl,
    date,
    description,
    mediaToRender,
    isEvent = false,
    authors = [],
    tags = [],
  } = tile;
  const { siteSettings } = useSettings();

  function getSearchPageSlug(pageCollection) {
    const slug = pageCollection?.find((p) => p.type == "think-articletopics")
      ?.url?.slug;

    return slug;
  }
  const tileClass = size.toLowerCase();
  const showLargeMedia = mediaToRender && (isBlue || size === TileSize.LARGE);
  const showSmallMedia = mediaToRender && !isBlue && size === TileSize.SMALL;

  return (
    <div
      className={clsx(
        styles["editor-choice-tile"],
        styles.tile,
        styles[tileClass],
        isBlue && styles.blue,
        hasImage && styles["tile--with-image"]
      )}
    >
      {hasImage && tile?.image?.fields && (
        <div className={clsx(styles["tile-image"], "relative")}>
          <NextContentfulImage
            alt={tile.image.fields.altText}
            src={tile.image.fields.url}
            width={tile.image.fields.width}
            height={tile.image.fields.height}
          />
          {showLargeMedia && (
            <EditorsChoiceMedia mediaToRender={mediaToRender} size={size} />
          )}
        </div>
      )}
      <div className={styles.content}>
        {isEvent && (
          <div className={styles["date-indicator"]}>
            <time dateTime={format(date, "yyyy-MM-dd")}>
              <span>{format(date, "MMM")}</span>
              {format(date, "dd")}
            </time>
          </div>
        )}
        <div className={styles["heading-container"]}>
          {showSmallMedia && (
            <EditorsChoiceMedia mediaToRender={mediaToRender} size={size} />
          )}
          <h2 className={styles.heading}>
            <Link href={linkUrl}>{title}</Link>
          </h2>
        </div>
        <div className={styles.details}>
          <time
            className={styles["tile-date"]}
            dateTime={format(date, "yyyy-MM-dd")}
          >
            {format(date, "dd MMMM yyyy")}
          </time>
          {authors && (
            <span className={styles["tile-author"]}>
              By {commaConjunction.format(authors)}
            </span>
          )}
        </div>
        {hasDescription && (
          <p className={styles["tile-description"]}>{description}</p>
        )}
        <ul className={styles.tags}>
          {tags != null &&
            tags.map((tag, index) => {
              const url = `/${getSearchPageSlug(
                siteSettings?.searchResultsPagesCollection?.items
              )}?${tag.queryParameter}=${tag.displayNameAndParamValue}`;
              return (
                <li key={index} className={clsx("tag", styles.tag)}>
                  <Link href={url}>{tag.displayNameAndParamValue}</Link>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
};

export default EditorsChoiceTile;
